"use client";

import { usePathname } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

import {
  isEligibleForWorkIntentionSurvey,
  type NotificationDataForWorkIntentionSurvey,
} from "./workIntentionSurveyEligibility";

const STORAGE_KEY_VERSION = "v1";

const dismissedInSession = new Set<string>();

const buildStorageKey = (workerId: string): string =>
  `${STORAGE_KEY_VERSION}:workIntentionSurveyModal:${workerId}`;

export function shouldShowWorkIntentionSurveyModal(params: {
  pathname: string;
  data: NotificationDataForWorkIntentionSurvey | null | undefined;
  loading: boolean;
  error: unknown;
  isDismissed?: boolean;
  now?: Date;
}): boolean {
  const { pathname, data, loading, error, isDismissed = false, now } = params;
  if (loading || error) return false;
  if (pathname !== "/work") return false;
  if (!data?.viewer?.id) return false;
  if (isDismissed) return false;
  return isEligibleForWorkIntentionSurvey(data, now);
}

export function useWorkIntentionSurveyModal(
  data: NotificationDataForWorkIntentionSurvey | null | undefined,
  loading: boolean,
  error: unknown,
) {
  const pathname = usePathname();
  const [isDismissed, setIsDismissed] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  const workerId = data?.viewer?.id;

  useEffect(() => {
    if (!workerId) {
      setIsLoaded(false);
      return;
    }

    try {
      const stored = localStorage.getItem(buildStorageKey(workerId));
      setIsDismissed(stored === "true" || dismissedInSession.has(workerId));
    } catch (readError) {
      console.warn("Failed to read from localStorage:", readError);
      setIsDismissed(dismissedInSession.has(workerId));
    } finally {
      setIsLoaded(true);
    }
  }, [workerId]);

  const shouldShowModal = useCallback(
    () => {
      if (!isLoaded) return false;
      return shouldShowWorkIntentionSurveyModal({
        pathname,
        data,
        loading,
        error,
        isDismissed,
      });
    },
    [isLoaded, isDismissed, pathname, data, loading, error],
  );

  const markDismissedForSession = useCallback(() => {
    if (!workerId) return;

    dismissedInSession.add(workerId);
    setIsDismissed(true);

    try {
      localStorage.setItem(buildStorageKey(workerId), "true");
    } catch (writeError) {
      console.warn("Failed to write to localStorage:", writeError);
    }
  }, [workerId]);

  return {
    shouldShowModal,
    markDismissedForSession,
    isLoaded: Boolean(workerId) && isLoaded,
  };
}

/** テスト用: セッション・localStorage 状態をリセット */
export function resetWorkIntentionSurveyDismissedInSessionForTests(): void {
  dismissedInSession.clear();
}
